import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Container, Form, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUser } from "../../../apis/user/UserProvider";
import { getErrorMessage, getPageName, getUiInterface } from "../../../locales/translationUtils";
import { FILTRO_TODOS_OS_USUARIOS, UsuarioResponse } from "../../../services/userService";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import AlertOverlay from "../../../components/Alert";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import User from "../../../types/user";

/** Critério de agrupamento das contas suspeitas de duplicidade. */
type Criterio = "email" | "nome";

interface Grupo {
    chave: string;
    usuarios: User[];
}

const normaliza = (valor?: string | null): string => (valor || "").trim().toLowerCase();

/**
 * Agrupa as contas que compartilham o mesmo e-mail ou o mesmo nome completo.
 * São os dois sinais de duplicidade que os dados realmente carregam: o login é
 * sempre diferente (o banco não deixaria repetir), então ele nunca acusa nada.
 */
const agrupaDuplicados = (usuarios: User[], criterio: Criterio): Grupo[] => {
    const porChave = new Map<string, User[]>();

    usuarios.forEach((usuario) => {
        const chave = normaliza(criterio === "email" ? usuario.email : usuario.fullName);

        if (!chave) return;

        porChave.set(chave, [...(porChave.get(chave) || []), usuario]);
    });

    return Array.from(porChave.entries())
        .filter(([, membros]) => membros.length > 1)
        .map(([chave, membros]) => ({
            chave,
            usuarios: [...membros].sort((a, b) => (a.id || 0) - (b.id || 0)),
        }))
        .sort((a, b) => b.usuarios.length - a.usuarios.length || a.chave.localeCompare(b.chave));
};

const perfilDe = (usuario: UsuarioResponse): string =>
    usuario.secUserSecRoles?.map((r) => r.secRole?.authority).filter(Boolean).join(", ") || "—";

/** Armazém/cliente no formato "ARMAZÉM › Cliente", ordenado para poder comparar linha a linha. */
const armazensDe = (usuario: UsuarioResponse): string[] =>
    (usuario.worksTo || [])
        .map((w) => `${w.wr?.name || "?"} › ${w.client?.name || "?"}`)
        .sort((a, b) => a.localeCompare(b, "pt-BR"));

const situacaoDe = (usuario: UsuarioResponse): string => {
    const marcas: string[] = [];
    if (usuario.enabled) marcas.push("Ativo");
    if (usuario.accountExpired) marcas.push("Conta expirada");
    if (usuario.accountLocked) marcas.push("Conta bloqueada");
    if (usuario.passwordExpired) marcas.push("Senha expirada");
    return marcas.length ? marcas.join(" · ") : "Inativo";
};

const CompareUsersView: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { fetchUsuarios, fetchUsuarioById, excluiUsuario } = useUser();

    const [usuarios, setUsuarios] = useState<User[]>([]);
    const [criterio, setCriterio] = useState<Criterio>("email");
    const [grupoAberto, setGrupoAberto] = useState<string | null>(null);
    const [detalhes, setDetalhes] = useState<UsuarioResponse[]>([]);
    const [carregandoDetalhes, setCarregandoDetalhes] = useState(false);
    const [usuarioParaExcluir, setUsuarioParaExcluir] = useState<UsuarioResponse | null>(null);
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" } | null>(null);

    const carregaUsuarios = useCallback(async () => {
        try {
            // Inclui inativos: conta duplicada costuma ter sido desativada em vez de excluída.
            setUsuarios(await fetchUsuarios(FILTRO_TODOS_OS_USUARIOS));
        } catch (err) {
            console.error("Erro ao carregar usuários", err);
            setAlert({ message: getErrorMessage("fetchUserError"), variant: "danger" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        carregaUsuarios();
    }, [carregaUsuarios]);

    const grupos = useMemo(() => agrupaDuplicados(usuarios, criterio), [usuarios, criterio]);

    const totalContasEnvolvidas = useMemo(
        () => grupos.reduce((soma, grupo) => soma + grupo.usuarios.length, 0),
        [grupos]
    );

    const abreGrupo = async (grupo: Grupo) => {
        if (grupoAberto === grupo.chave) {
            setGrupoAberto(null);
            setDetalhes([]);
            return;
        }

        setGrupoAberto(grupo.chave);
        setCarregandoDetalhes(true);
        setDetalhes([]);

        try {
            // A listagem não traz armazéns/clientes; o comparativo precisa do detalhe de cada conta.
            const respostas = await Promise.all(
                grupo.usuarios.map((u) => fetchUsuarioById(u.id!))
            );

            setDetalhes(respostas.map((r) => r?.data).filter(Boolean) as UsuarioResponse[]);
        } catch (err) {
            console.error("Erro ao carregar os detalhes do grupo", err);
            setAlert({ message: getErrorMessage("fetchUserByIdError"), variant: "danger" });
        } finally {
            setCarregandoDetalhes(false);
        }
    };

    const confirmaExclusao = async () => {
        const alvo = usuarioParaExcluir;
        setUsuarioParaExcluir(null);

        if (!alvo) return;

        const sucesso = await excluiUsuario(alvo.id);

        if (!sucesso) {
            setAlert({ message: getErrorMessage("deleteUsuarioError", { id: alvo.id }), variant: "danger" });
            return;
        }

        setAlert({
            message: t("user.compare.deleted", { user: alvo.username }),
            variant: "success",
        });

        setDetalhes((prev) => prev.filter((u) => u.id !== alvo.id));
        setUsuarios((prev) => prev.filter((u) => u.id !== alvo.id));
    };

    const navItems: NavBarItem[] = [
        { label: t("user.labels.home"), route: "/", iconName: "house-door" },
        { label: t("user.labels.list"), route: "/user", iconName: "list" },
        { label: t("user.labels.new"), route: "/user/create", iconName: "plus-circle" },
    ];

    /** Uma linha do comparativo. Marca a linha quando as contas divergem naquele atributo. */
    const renderLinha = (rotulo: string, valores: string[]) => {
        const divergem = new Set(valores).size > 1;

        return (
            <tr key={rotulo}>
                <th
                    scope="row"
                    style={{
                        whiteSpace: "nowrap",
                        backgroundColor: divergem ? "#fff8e1" : undefined,
                    }}
                >
                    {rotulo}
                    {divergem && (
                        <Badge bg="warning" text="dark" className="ms-2">
                            {t("user.compare.differs")}
                        </Badge>
                    )}
                </th>
                {valores.map((valor, i) => (
                    <td
                        key={i}
                        // pre-line preserva a quebra entre os armazéns da mesma conta.
                        style={{
                            backgroundColor: divergem ? "#fff8e1" : undefined,
                            whiteSpace: "pre-line",
                        }}
                    >
                        {valor}
                    </td>
                ))}
            </tr>
        );
    };

    return (
        <Container fluid style={{ height: "calc(100vh - 200px)", overflow: "auto" }}>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("compareUsers")}</h2>
            <p className="text-muted" style={{ maxWidth: "70ch" }}>
                {t("user.compare.help")}
            </p>

            <div className="d-flex gap-4 align-items-center mb-3">
                <Form.Group>
                    <Form.Label className="mb-1">{t("user.compare.criterion")}</Form.Label>
                    <Form.Select
                        value={criterio}
                        onChange={(e) => {
                            setCriterio(e.target.value as Criterio);
                            setGrupoAberto(null);
                            setDetalhes([]);
                        }}
                        style={{ width: "260px" }}
                    >
                        <option value="email">{t("user.compare.byEmail")}</option>
                        <option value="nome">{t("user.compare.byName")}</option>
                    </Form.Select>
                </Form.Group>

                <div className="text-muted" style={{ paddingTop: "26px" }}>
                    {t("user.compare.summary", { groups: grupos.length, users: totalContasEnvolvidas })}
                </div>
            </div>

            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}

            {grupos.length === 0 && (
                <div className="alert alert-success">{t("user.compare.none")}</div>
            )}

            {grupos.map((grupo) => {
                const aberto = grupoAberto === grupo.chave;

                return (
                    <div key={grupo.chave} className="border rounded mb-3">
                        <button
                            type="button"
                            className="btn w-100 text-start d-flex align-items-center gap-3 p-3"
                            onClick={() => abreGrupo(grupo)}
                            aria-expanded={aberto}
                        >
                            <i className={`bi bi-chevron-${aberto ? "down" : "right"}`}></i>
                            <span className="fw-bold">{grupo.chave}</span>
                            <Badge bg="secondary">
                                {t("user.compare.accounts", { count: grupo.usuarios.length })}
                            </Badge>
                            <span className="text-muted ms-auto">
                                {grupo.usuarios.map((u) => u.username).join(", ")}
                            </span>
                        </button>

                        {aberto && (
                            <div className="p-3 pt-0">
                                {carregandoDetalhes && (
                                    <div className="text-muted">{t("user.compare.loading")}</div>
                                )}

                                {!carregandoDetalhes && detalhes.length > 0 && (
                                    <div style={{ overflowX: "auto" }}>
                                        <Table bordered size="sm" className="mb-0" style={{ minWidth: "700px" }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ width: "180px" }}></th>
                                                    {detalhes.map((u) => (
                                                        <th key={u.id}>
                                                            <div className="d-flex flex-column gap-2">
                                                                <span>{u.username}</span>
                                                                <div className="d-flex gap-2">
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-primary"
                                                                        onClick={() => navigate(`/user/${u.id}`)}
                                                                    >
                                                                        {getUiInterface("edit")}
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="outline-danger"
                                                                        onClick={() => setUsuarioParaExcluir(u)}
                                                                    >
                                                                        {getUiInterface("delete")}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {renderLinha(t("user.fields.id"), detalhes.map((u) => String(u.id)))}
                                                {renderLinha(t("user.fields.name"), detalhes.map((u) => u.fullName || "—"))}
                                                {renderLinha(t("user.fields.email"), detalhes.map((u) => u.email || "—"))}
                                                {renderLinha(t("user.fields.role"), detalhes.map(perfilDe))}
                                                {renderLinha(
                                                    t("user.compare.status"),
                                                    detalhes.map(situacaoDe)
                                                )}
                                                {renderLinha(
                                                    t("user.compare.warehouseCount"),
                                                    detalhes.map((u) => String(armazensDe(u).length))
                                                )}
                                                {renderLinha(
                                                    t("user.compare.warehouses"),
                                                    detalhes.map((u) => armazensDe(u).join("\n") || "—")
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            <ConfirmDeleteModal
                show={!!usuarioParaExcluir}
                onHide={() => setUsuarioParaExcluir(null)}
                onConfirm={confirmaExclusao}
            />
        </Container>
    );
};

export default CompareUsersView;
